"use client"

import HomeDetailsWrapperAccordion from "@/features/homeowners/components/home-details-wrapper-accordion"
import { useWorkscopeLabelStyle, WORKSCOPE_LABEL_COLORS } from "../home-details-wrapper-accordion"
import FormTextareaInput from "../../../../shared/components/form-inputs/form-textarea-input"
import DateStampedTextarea from "../../../../shared/components/form-inputs/form-date-stamped-textarea-input"
import FormSelectInput from "../../../../shared/components/form-inputs/form-select-input"
import WorkScopeStatusSelect from "../work-scope-status-select"
import FormMultiSelectInput from "../../../../shared/components/form-inputs/form-multi-select-input"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import { workScopeStatusOptions } from "../../constants/options"
import { HomeWithHomeOwnerFormType } from "../../schema/home-with-home-owner.schema"
import { useFormContext, useWatch } from "react-hook-form"
import { useClaimedByOptions } from "../../hooks/use-claimed-by-options"
import { useWorkscopeClaimedBySync } from "../../hooks/use-workscope-claimed-by-sync"
import { GoodwillToggleLabel } from "../goodwill-toggle-label"

const goodwillGreenStyle = { color: WORKSCOPE_LABEL_COLORS.goodwill } as const
const BASE_FIELD_NAME = "home.crawlSpaceInfo"

function HomeCrawlSpaceForm() {
    const goodwillLabelStyle = useWorkscopeLabelStyle(true) ?? goodwillGreenStyle
    const { control, setValue, getValues } = useFormContext<HomeWithHomeOwnerFormType>()
    const { options: claimedByOptions, loading: claimedByLoading } = useClaimedByOptions()
    const goodwillCrawlSpaceMuckOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillCrawlSpaceMuckOn` })
    const goodwillCrawlSpaceInsulationOn = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillCrawlSpaceInsulationOn` })
    
    // Watch status fields and claimed by fields
    const crawlSpaceMuck = useWatch({ control, name: `${BASE_FIELD_NAME}.crawlSpaceMuck` })
    const crawlSpaceMuckClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.crawlSpaceMuckClaimedBy` })
    const crawlSpaceInsulation = useWatch({ control, name: `${BASE_FIELD_NAME}.crawlSpaceInsulation` })
    const crawlSpaceInsulationClaimedBy = useWatch({ control, name: `${BASE_FIELD_NAME}.crawlSpaceInsulationClaimedBy` })
    const goodwillCrawlSpaceMuck = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillCrawlSpaceMuck` })
    const goodwillCrawlSpaceMuckClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillCrawlSpaceMuckClaimedBy`,
    })
    const goodwillCrawlSpaceInsulation = useWatch({ control, name: `${BASE_FIELD_NAME}.goodwillCrawlSpaceInsulation` })
    const goodwillCrawlSpaceInsulationClaimedBy = useWatch({
        control,
        name: `${BASE_FIELD_NAME}.goodwillCrawlSpaceInsulationClaimedBy`,
    })

    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: crawlSpaceMuck,
        claimedBy: crawlSpaceMuckClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.crawlSpaceMuckClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: crawlSpaceInsulation,
        claimedBy: crawlSpaceInsulationClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.crawlSpaceInsulationClaimedBy`,
        setValue,
        getValues,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillCrawlSpaceMuck,
        claimedBy: goodwillCrawlSpaceMuckClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillCrawlSpaceMuckClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillCrawlSpaceMuckOn,
    })
    useWorkscopeClaimedBySync<HomeWithHomeOwnerFormType>({
        status: goodwillCrawlSpaceInsulation,
        claimedBy: goodwillCrawlSpaceInsulationClaimedBy,
        claimedByFieldPath: `${BASE_FIELD_NAME}.goodwillCrawlSpaceInsulationClaimedBy`,
        setValue,
        getValues,
        enabled: !!goodwillCrawlSpaceInsulationOn,
    })

    return (
        <HomeDetailsWrapperAccordion
            heading="Crawl Space"
            wrapperClassName="xl:grid-cols-2"
        >
            <WorkScopeStatusSelect workScopeSlug="m-and-g-crawl-space"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Crawl Space Muck" toggleName={`${BASE_FIELD_NAME}.goodwillCrawlSpaceMuckOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.crawlSpaceMuck`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Muck Claimed By",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.crawlSpaceMuckClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillCrawlSpaceMuckOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Crawl Space Muck Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillCrawlSpaceMuck`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Crawl Space Muck Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillCrawlSpaceMuckClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            <WorkScopeStatusSelect workScopeSlug="insulate-crawl-space"
                inputGroupProps={{
                    label: <GoodwillToggleLabel title="Crawl Space Insulation" toggleName={`${BASE_FIELD_NAME}.goodwillCrawlSpaceInsulationOn` as any} control={control} />,
                }}
                options={workScopeStatusOptions}
                name={`${BASE_FIELD_NAME}.crawlSpaceInsulation`}
                control={control}
            />
            <FormMultiSelectInput
                inputGroupProps={{
                    label: "Insulation Claimed By",
                }}
                options={claimedByOptions}
                name={`${BASE_FIELD_NAME}.crawlSpaceInsulationClaimedBy`}
                control={control}
                placeholder={claimedByLoading ? "Loading options..." : "Select"}
                searchPlaceholder="Search options..."
            />
            {goodwillCrawlSpaceInsulationOn && (
                <>
                    <FormSelectInput
                        inputGroupProps={{ label: "Crawl Space Insulation Goodwill", labelStyle: goodwillLabelStyle }}
                        options={workScopeStatusOptions}
                        name={`${BASE_FIELD_NAME}.goodwillCrawlSpaceInsulation`}
                        control={control}
                    />
                    <FormMultiSelectInput
                        inputGroupProps={{ label: "Crawl Space Insulation Goodwill Claimed By" }}
                        options={claimedByOptions}
                        name={`${BASE_FIELD_NAME}.goodwillCrawlSpaceInsulationClaimedBy`}
                        control={control}
                        placeholder={claimedByLoading ? "Loading options..." : "Select"}
                        searchPlaceholder="Search options..."
                    />
                </>
            )}
            <FormTextInput
                inputGroupProps={{
                    label: "Square feet of crawl space insulation needed",
                }}
                placeholder="Square feet of crawl space insulation needed"
                name={`${BASE_FIELD_NAME}.crawlSpaceSquareFeet`}
                type="number"
                control={control}
            />
            <DateStampedTextarea
                includeUserName
                inputGroupProps={{
                    label: "Description",
                    wrapperClassName: "xl:col-span-2"
                }}
                rows={3}
                placeholder="Describe the crawl space work needed (muck removal and insulation)"
                name={`${BASE_FIELD_NAME}.description`}
                control={control}
            />
        </HomeDetailsWrapperAccordion>
    )
}

export default HomeCrawlSpaceForm 
